// hooks/useUrlFilters.js
import { useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';

const isEmptyVal = (v) =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const cleanObject = (obj = {}) =>
    Object.keys(obj).reduce((acc, k) => {
        if (!isEmptyVal(obj[k])) acc[k] = obj[k];
        return acc;
    }, {});

// Helpers format
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const toDateObj = (d) => {
    if (!d) return null;
    if (typeof d?.toDate === 'function') return d.toDate();     // dayjs/moment
    if (typeof d?.format === 'function') return new Date(d.format()); // moment string
    if (d instanceof Date) return isNaN(d) ? null : d;
    if (typeof d === 'string' || typeof d === 'number') {
        const dt = new Date(d);
        return isNaN(dt) ? null : dt;
    }
    return null;
};

const fmtDate = (d, format = 'YYYY-MM-DD') => {
    if (!d) return '';
    if (typeof d?.format === 'function') return d.format(format); // moment/dayjs
    const dt = toDateObj(d);
    if (!dt) return '';
    const Y = dt.getFullYear();
    const M = pad(dt.getMonth() + 1);
    const D = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const ss = pad(dt.getSeconds());
    if (format === 'YYYY-MM-DD') return `${Y}-${M}-${D}`;
    if (format === 'YYYY-MM-DD HH:mm:ss') return `${Y}-${M}-${D} ${hh}:${mm}:${ss}`;
    // fallback: chỉ trả về ISO nếu format lạ
    return dt.toISOString();
};

const defaultDateFormat = 'YYYY-MM-DD';
const defaultDateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

/**
 * useUrlFilters
 *
 * Hook đồng bộ bộ lọc lên URL (query string) và cung cấp API thao tác filter.
 * Hỗ trợ field đơn lẻ và field dạng khoảng (range) qua cấu hình `rangeMap`.
 *
 * @param array $options {
 *   Tuỳ chọn cấu hình cho hook.
 *
 *   @type array  $rangeMap          Bản đồ các field dạng khoảng -> 2 tham số URL (from/to).
 *                                   Định nghĩa cho MỖI key trong $rangeMap:
 *                                   [
 *                                     'from'      => string,  // tên param cận dưới trên URL (vd: 'price_min')
 *                                     'to'        => string,  // tên param cận trên trên URL (vd: 'price_max')
 *                                     'type'      => 'date'|'datetime'|'number'|'string'|'iso', // mặc định: 'date'
 *                                     'format'    => string,  // định dạng output cho 'date'/'datetime' (vd: 'YYYY-MM-DD HH:mm:ss')
 *                                     'endOfDay'  => bool,    // nếu true và type='datetime': đặt cận trên 23:59:59.999
 *                                     'keepKey'   => bool,    // nếu true: vẫn giữ key gốc trên URL (mặc định: false)
 *                                     'serialize' => callable // hàm tuỳ biến hoàn toàn: fn($start, $end) => ['fromValue'=>..., 'toValue'=>...]
 *                                   ]
 *
 *   @type string $dateFormat        Định dạng mặc định cho kiểu 'date'. Mặc định: 'YYYY-MM-DD'.
 *   @type string $dateTimeFormat    Định dạng mặc định cho kiểu 'datetime'. Mặc định: 'YYYY-MM-DD HH:mm:ss'.
 *   @type array  $initialExtra      Query mặc định giữ lại khi clearAll (vd: { page:1, per_page:30 }).
 *   @type string $mode              Cách cập nhật URL: 'push' | 'replace'. Mặc định: 'push'.
 * }
 *
 * @return array {
 *   Mảng các giá trị/hàm tiện ích để thao tác filter.
 *
 *   @type object   $query                 Toàn bộ query hiện tại (đã parse từ URL + initialExtra).
 *   @type callable $handleFilterChange    Cập nhật 1 filter.
 *                                         Signature: fn(string $name, mixed $value): void
 *                                         - Nếu $name là field thường: $value là giá trị bất kỳ.
 *                                         - Nếu $name nằm trong rangeMap: $value là mảng [start, end].
 *
 *   @type callable $setFilters            Cập nhật nhiều filter cùng lúc.
 *                                         Signature: fn(object $hash): void
 *                                         - $hash: { fieldA: valueA, rangeFieldB: [start, end], ... }
 *
 *   @type callable $clearFilter           Xoá 1 filter (và cặp from/to nếu là range).
 *                                         Signature: fn(string $name): void
 *
 *   @type callable $clearAll              Xoá toàn bộ filter, trả URL về $initialExtra.
 *                                         Signature: fn(): void
 * }
 *
 * @example
 * // Cấu hình đa dạng cho rangeMap:
 * useUrlFilters({
 *   initialExtra: { page: 1, per_page: 30 },
 *   rangeMap: {
 *     // 1) Ngày gửi: date-only
 *     send_date: { from: 'send_date_from', to: 'send_date_to', type: 'date' },
 *
 *     // 2) Ngày tạo: datetime + bao phủ cuối ngày
 *     created_at: { from: 'created_at_from', to: 'created_at_to', type: 'datetime', endOfDay: true },
 *
 *     // 3) Giá: number
 *     price: { from: 'price_min', to: 'price_max', type: 'number' },
 *
 *     // 4) ISO datetime (phù hợp API cần ISO 8601)
 *     updated_at: { from: 'updated_at_gte', to: 'updated_at_lte', type: 'iso' },
 *
 *     // 5) Tuỳ biến hoàn toàn bằng serialize (ví dụ làm tròn doanh thu)
 *     revenue: {
 *       from: 'revenue_start',
 *       to: 'revenue_end',
 *       serialize: (start, end) => ({
 *         fromValue: (start || start === 0) ? Math.round(Number(start)) : null,
 *         toValue:   (end   || end   === 0) ? Math.round(Number(end))   : null,
 *       }),
 *     },
 *
 *     // 6) Giữ cả key gốc trên URL (không thường dùng)
 *     month: { from: 'month_from', to: 'month_to', type: 'date', format: 'YYYY-MM', keepKey: true },
 *   },
 * });
 */

export default function useUrlFilters({
    rangeMap = {},
    dateFormat = defaultDateFormat,
    dateTimeFormat = defaultDateTimeFormat,
    initialExtra = {},
    mode = 'push',
} = {}) {
    const history = useHistory();
    const location = useLocation();

    const query = useMemo(() => {
        const q = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
        return { ...initialExtra, ...q };
    }, [location.search, initialExtra]);

    const pushQuery = useCallback(
        (nextQ) => {
            const cleaned = cleanObject(nextQ);
            const search = qs.stringify(cleaned);
            const nav = { pathname: location.pathname, search };
            if (mode === 'replace') history.replace(nav);
            else history.push(nav);
        },
        [history, location.pathname, mode]
    );

    const normalizeRange = (name, rawValue) => {
        const cfg = rangeMap[name];
        if (!cfg) return null;

        const { from, to, type = 'date', format, endOfDay = false, serialize } = cfg;
        const [start, end] = Array.isArray(rawValue) ? rawValue : [];

        if (typeof serialize === 'function') {
            const { fromValue, toValue } = serialize(start, end);
            return { fromKey: from, toKey: to, fromValue, toValue };
        }

        let fromValue = null, toValue = null;

        switch (type) {
            case 'number':
                fromValue = start === 0 || start ? Number(start) : null;
                toValue = end === 0 || end ? Number(end) : null;
                break;

            case 'string':
                fromValue = start || null;
                toValue = end || null;
                break;

            case 'iso':
                fromValue = start ? toDateObj(start)?.toISOString() : null;
                toValue = end ? toDateObj(end)?.toISOString() : null;
                break;

            case 'datetime': {
                const fmt = format || dateTimeFormat;
                const s = toDateObj(start);
                const e = toDateObj(end);
                fromValue = s ? fmtDate(s, fmt) : null;
                if (e) {
                    if (endOfDay) {
                        // set 23:59:59 cho cận trên
                        e.setHours(23, 59, 59, 999);
                    }
                    toValue = fmtDate(e, fmt);
                } else {
                    toValue = null;
                }
                break;
            }

            case 'month-day': {
                // Trích xuất MM-DD từ ngày
                const s = toDateObj(start);
                const e = toDateObj(end);

                fromValue = s ? `${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}` : null;
                toValue = e ? `${String(e.getMonth() + 1).padStart(2, '0')}-${String(e.getDate()).padStart(2, '0')}` : null;
                break;
            }

            case 'date':
            default: {
                const fmt = format || dateFormat;
                const s = start ? fmtDate(start, fmt) : '';
                let e = end ? fmtDate(end, fmt) : '';
                // Nếu muốn bao phủ end-of-day mà backend dùng date-only,
                // hãy đổi type='datetime' + endOfDay=true ở cấu hình.
                fromValue = s || null;
                toValue = e || null;
                break;
            }
        }

        return { fromKey: from, toKey: to, fromValue, toValue };
    };

    /**
     * handleFilterChange
     *
     * Cập nhật 1 filter và đồng bộ lên URL.
     * - Nếu $name thuộc rangeMap: $value phải là [start, end], hook sẽ map -> from/to.
     * - Nếu $name là field thường: $value là giá trị trực tiếp.
     *
     * @param string $name   Tên filter (có thể là key trong rangeMap hoặc field thường).
     * @param mixed  $value  Giá trị filter:
     *                       - range: [start, end] (Date | moment/dayjs | string | number)
     *                       - thường: bất kỳ
     * @return void
     *
     * @example handleFilterChange('keyword', 't-shirt');
     * @example handleFilterChange('created_at', [dayjs('2025-08-01'), dayjs('2025-08-20')]);
     * @example handleFilterChange('price', [10, 99]);
     */
    const handleFilterChange = useCallback(
        (name, value) => {
            let q = { ...query, [name]: value };

            if (rangeMap[name]) {
                const mapped = normalizeRange(name, value);
                if (mapped) {
                    const { fromKey, toKey, fromValue, toValue } = mapped;
                    if (fromValue !== null && fromValue !== undefined && fromValue !== '') q[fromKey] = fromValue;
                    else delete q[fromKey];

                    if (toValue !== null && toValue !== undefined && toValue !== '') q[toKey] = toValue;
                    else delete q[toKey];

                    if (!rangeMap[name].keepKey) delete q[name];
                }
            }

            q = cleanObject(q);
            pushQuery(q);
        },
        [query, rangeMap, pushQuery]
    );

    // useUrlFilters.js
    const handleFilterChanges = (changes = {}) => {
        // lấy query hiện tại từ location.search (đang có sẵn trong hook)
        let q = qs.parse(location.search.slice(1));

        Object.entries(changes).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
                delete q[k];
            } else {
                q[k] = v;
            }
        });

        q = cleanObject(q);

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };


    const setFilters = useCallback(
        (hash = {}) => {
            let q = { ...query };
            Object.keys(hash).forEach((name) => {
                const val = hash[name];

                if (rangeMap[name]) {
                    const mapped = normalizeRange(name, val);
                    if (mapped) {
                        const { fromKey, toKey, fromValue, toValue } = mapped;
                        if (fromValue !== null && fromValue !== undefined && fromValue !== '') q[fromKey] = fromValue;
                        else delete q[fromKey];
                        if (toValue !== null && toValue !== undefined && toValue !== '') q[toKey] = toValue;
                        else delete q[toKey];
                        if (!rangeMap[name].keepKey) delete q[name];
                        return;
                    }
                }

                if (isEmptyVal(val)) delete q[name];
                else q[name] = val;
            });
            pushQuery(q);
        },
        [query, rangeMap, pushQuery]
    );

    const clearFilter = useCallback(
        (name) => {
            let q = { ...query };
            if (rangeMap[name]) {
                const { from, to } = rangeMap[name];
                delete q[from];
                delete q[to];
                if (!rangeMap[name].keepKey) delete q[name];
            } else {
                delete q[name];
            }
            pushQuery(q);
        },
        [query, rangeMap, pushQuery]
    );

    const clearAll = useCallback(() => {
        pushQuery(initialExtra || {});
    }, [pushQuery, initialExtra]);

    return { query, handleFilterChange, handleFilterChanges, setFilters, clearFilter, clearAll };
}
